// D98.2 - Delivery tab. Props (CIP passes its existing handlers/values):
//   items                  = invoice.items
//   deliverySlipSection    = invoice.deliverySlipSection ({ shipTo, slips })
//   summary                = { deliverySlipCount, dispatchedQuantityTotal, pendingDispatchQuantityTotal }
//   updateItem(index, { dispatchQuantity } | { dispatchPacking })   = the page's updateItem; dispatchPacking
//                            is the slip's packing text (null = untyped, the box shows Packing details, D153.2)
//   readOnly               = the user cannot edit invoices: inputs disabled, Create / Exchange hidden
//   cancelled              = D96.18: inputs disabled, Exchange hidden, Create stays but disabled and says why
//   sentOnward             = invoiceTotals.isInvoiceSentOnward(invoice.status): same as cancelled — inputs
//                            disabled, Exchange hidden, Create disabled and says why (a sent invoice makes
//                            no new slip, D153.11); printing stays
//   creatingSlip           = a slip is being created
//   onCreateSlip()         = createDeliverySlip: saves the page (packing text included), then sends the
//                            on-screen staged quantities in the slip request (F34)
//   onPrintSlip(slip)      = the page opens that slip's server PDF by slip.slipId, unsaved changes or not (D153.5)
//   onPrintSlipPackingList(slip) = the page opens that delivery slip's own packing list (D158.2; none for an exchange slip)
//   onPrintPackingList()   = the page opens the packing-list server PDF (D153.5)
//   onExchangeSubmit(a)    = the page's exchange-slip action; exchangeBusy disables the form
//   creditNotes            = invoice credit notes (Item 18, 9 Oct QA retest): a SHORT-DELIVERY one
//                            already takes the credited quantity off "remaining", so the tab stops
//                            offering packs a credit note already wrote off
//   stageLines(list)       = D158.1: the page's ONE batched edit (one undo step) of [{ index, dispatchQuantity }];
//                            select all / clear. A line's own tick goes through updateItem.
//   orderItems             = the page-loaded order's lines, read by position (confirmedOrderItemIndex) for the
//                            group filter; undefined = not loaded. A tick is derived: staged > 0, nothing stored.
import React from 'react';
import { ExchangeSlipForm } from './ExchangeSlipForm';
import { num } from '../../utils/invoiceLineMath';
import type { ExchangeAnswers } from './worksheetPrompts';
import { billedAfterDispatch, invoicedQty, countUndispatchedLines, remainingDispatchQty, shortDeliveryCreditedQtyByLine } from '../../utils/clientInvoiceDispatch';
import { formatDateTime } from '../../utils/dateFormat';
import { DELIVERY_FIELD_NOTES } from './deliveryFieldNotes';
import { TILES, tileOf } from '../confirmedOrders/sectionTotals';
import { isMarkedNotSupplied } from '../../utils/clientInvoiceLine';
import { readsAsInventory, INVENTORY_SUPPLIER } from './inventorySupplier';

type Props = {
  items: ReadonlyArray<any>;
  deliverySlipSection?: { shipTo?: string; slips?: any[] } | null;
  summary: { deliverySlipCount?: number; dispatchedQuantityTotal?: number; pendingDispatchQuantityTotal?: number };
  updateItem: (index: number, patch: { dispatchQuantity: number } | { dispatchPacking: string }, typing?: boolean) => void;
  readOnly: boolean;
  cancelled?: boolean;
  sentOnward?: boolean;
  creatingSlip?: boolean;
  onCreateSlip: () => void;
  onPrintSlip: (slip: any) => void;
  onPrintSlipPackingList: (slip: any) => void;
  onPrintPackingList: () => void;
  onExchangeSubmit: (answers: ExchangeAnswers) => void;
  exchangeBusy?: boolean;
  creditNotes?: ReadonlyArray<any>;
  stageLines: (quantities: ReadonlyArray<{ index: number; dispatchQuantity: number }>) => void;
  orderItems?: ReadonlyArray<any>;
  /** D157.17: a customs invoice ships nothing; the tab offers its customs slip print instead of the staging table. */
  customs?: boolean;
  onPrintCustomsSlip?: () => void;
};

const btn = 'rounded-md border px-3 py-1.5 text-xs font-semibold';
const secondary = `${btn} border-slate-200 bg-white text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400`;

// Blank or junk is 0, as the old input read it.
const qtyOf = (text: string) => num(text) ?? 0;

const ALL = 'all';
const COLD = 'cold';
const NO_ORDER_LINE = 'no_order_line';
// D158.1: a line's group is its order line's category, named as the order page's tiles name it (tileOf).
const groupOf = (item: any, orderItems: ReadonlyArray<any> | undefined) => {
  const orderLine = item?.confirmedOrderItemIndex == null ? null : orderItems?.[item.confirmedOrderItemIndex];
  return orderLine ? tileOf(orderLine.category) : NO_ORDER_LINE;
};

export function DeliveryTab(p: Props) {
  const [exchangeOpen, setExchangeOpen] = React.useState(false);
  const [filter, setFilter] = React.useState(ALL);
  const slips = p.deliverySlipSection?.slips || [];
  // Item 18: computed once per render, from whatever credit notes the page has loaded.
  const creditedShortByLine = shortDeliveryCreditedQtyByLine(p.creditNotes);
  // D104.43: counted in lines - the quantity totals mix KG, PCS and TIN. The same rule the
  // server's undispatched gate uses (countUndispatchedLines).
  const pending = countUndispatchedLines(p.items, creditedShortByLine);
  const lineCount = p.items.length;
  const count = p.summary.deliverySlipCount ?? slips.length;
  const hasDispatchRows = p.items.some((item) => qtyOf(String(item?.dispatchQuantity ?? '')) > 0);
  const frozen = p.readOnly || !!p.cancelled || !!p.sentOnward;
  const createOff = !hasDispatchRows || !!p.creatingSlip || !!p.cancelled || !!p.sentOnward;
  const createRefusal = p.cancelled
    ? 'This invoice is cancelled; a delivery slip cannot be made.'
    : p.sentOnward
      ? 'This invoice has been sent; a delivery slip cannot be made.'
      : undefined;

  // D158.1: tick = stage the remaining (D141 credits off); the tick is derived from the staged quantity.
  const remainingOf = (index: number) => remainingDispatchQty(p.items[index], creditedShortByLine.get(String(p.items[index].lineNumber ?? index + 1)));
  const stagedOf = (index: number) => qtyOf(String(p.items[index].dispatchQuantity ?? ''));
  const groups = p.items.map((item) => groupOf(item, p.orderItems));
  const filterOptions = [
    { value: ALL, label: 'All lines' },
    ...[...TILES.map((t) => t.key), NO_ORDER_LINE]
      .filter((key) => groups.includes(key))
      .map((key) => ({ value: key, label: TILES.find((t) => t.key === key)?.label ?? 'No order line' })),
    { value: COLD, label: 'Cold storage' },
  ];
  const activeFilter = filterOptions.some((o) => o.value === filter) ? filter : ALL;
  const shown = p.items
    .map((_, index) => index)
    .filter((index) => activeFilter === ALL || (activeFilter === COLD ? !!p.items[index].coldStorageFlag : groups[index] === activeFilter));
  const canStage = shown.filter((index) => remainingOf(index) > 0);
  const stagedShown = shown.filter((index) => stagedOf(index) > 0);
  const allTicked = canStage.length > 0 && canStage.every((index) => stagedOf(index) > 0);
  const toggleAll = () => {
    const quantities = allTicked
      ? stagedShown.map((index) => ({ index, dispatchQuantity: 0 }))
      : canStage.filter((index) => stagedOf(index) <= 0).map((index) => ({ index, dispatchQuantity: remainingOf(index) }));
    if (quantities.length > 0) p.stageLines(quantities);
  };

  if (p.customs) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
        <div className="text-sm text-slate-700">A customs invoice ships nothing, so it has no delivery slips. Print its customs slip for the vessel to sign.</div>
        <button type="button" onClick={p.onPrintCustomsSlip} className={secondary}>Print customs slip</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-700">
          <span className="font-semibold">{count} {count === 1 ? 'slip' : 'slips'}</span>
          {` · ${lineCount - pending} of ${lineCount} lines dispatched · ${pending} pending`}
          {p.deliverySlipSection?.shipTo ? <span className="ml-3 text-xs text-slate-500">Ship to: {p.deliverySlipSection.shipTo}</span> : null}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={p.onPrintPackingList} className={secondary}>Print packing list</button>
          {!frozen && (
            <button type="button" onClick={() => setExchangeOpen(true)} className={secondary}>Exchange slip…</button>
          )}
          {!p.readOnly && (
            <button
              type="button"
              onClick={p.onCreateSlip}
              disabled={createOff}
              title={createRefusal}
              className={`${btn} ${createOff ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400' : 'border-primary-500 bg-primary-500 text-white'}`}
            >
              {p.creatingSlip ? 'Creating...' : 'Create delivery slip'}
            </button>
          )}
        </div>
      </div>

      {exchangeOpen && !frozen && (
        <ExchangeSlipForm
          busy={p.exchangeBusy}
          onCancel={() => setExchangeOpen(false)}
          onSubmit={(a) => {
            p.onExchangeSubmit(a);
            setExchangeOpen(false);
          }}
        />
      )}

      {slips.length > 0 && (
        <div className="space-y-2">
          {slips.map((slip: any, index: number) => (
            <div key={slip.slipId || index} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <div>
                <div className="font-semibold text-slate-800">{slip.slipNumber || slip.slipId || `Slip ${index + 1}`}</div>
                {/* D153.10: no quantity here - a slip's rows are in different units. D158.3: a count of rows is not a sum. */}
                <div className="text-slate-500">
                  {formatDateTime(slip.createdAt, 'Pending')}
                  {Array.isArray(slip.items) && slip.items.length > 0 ? ` · ${slip.items.length} ${slip.items.length === 1 ? 'item' : 'items'}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => p.onPrintSlip(slip)} className={secondary}>Print slip</button>
                {!slip.isExchangeSlip && (
                  <button type="button" onClick={() => p.onPrintSlipPackingList(slip)} className={secondary}>Packing list</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <label className="flex items-center gap-2 text-xs text-slate-700">
        <span className="font-semibold">Show</span>
        <select
          aria-label="Show lines"
          title={DELIVERY_FIELD_NOTES.filter}
          value={activeFilter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-8 rounded border border-slate-200 bg-white px-2"
        >
          {filterOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>

      <div className="overflow-auto rounded-md border border-slate-200">
        <table className="data-table-grid w-full min-w-[1040px] text-xs">
          <thead className="sticky top-0 border-b border-slate-200 bg-slate-50">
            <tr className="text-left text-slate-600">
              <th className="px-3 py-3">
                <input
                  type="checkbox"
                  aria-label="Select all lines"
                  title={DELIVERY_FIELD_NOTES.selectAll}
                  checked={allTicked}
                  ref={(el) => { if (el) el.indeterminate = !allTicked && stagedShown.length > 0; }}
                  disabled={frozen || (canStage.length === 0 && stagedShown.length === 0)}
                  onChange={toggleAll}
                />
              </th>
              <th className="px-3 py-3">#</th>
              <th className="px-3 py-3">Item</th>
              <th className="px-3 py-3">Invoice Qty</th>
              <th className="px-3 py-3" title={DELIVERY_FIELD_NOTES.dispatched}>Dispatched Qty</th>
              <th className="px-3 py-3" title={DELIVERY_FIELD_NOTES.remaining}>Remaining Qty</th>
              <th className="px-3 py-3">Dispatch Qty</th>
              <th className="px-3 py-3">Packing on this slip</th>
              <th className="px-3 py-3">Unit</th>
              <th className="px-3 py-3">Source</th>
              <th className="px-3 py-3">Supplier</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((index) => {
              const item = p.items[index];
              const lineKey = String(item.lineNumber ?? index + 1);
              const staged = stagedOf(index);
              // D157.6: an unsent invoice's slip may send more than remains; billed rises by the extra (the server's sum).
              const { remainingBefore: remaining, extra, billedAfter: raisedTo } = billedAfterDispatch(item, staged, creditedShortByLine.get(lineKey) || 0);
              const billedAfter = !frozen && extra > 0 ? raisedTo : null;
              return (
                <tr key={index} className="border-b align-top">
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label={`Stage line ${item.lineNumber ?? index + 1}`}
                      title={DELIVERY_FIELD_NOTES.stage}
                      checked={staged > 0}
                      disabled={frozen || (staged <= 0 && remaining <= 0)}
                      onChange={() => p.updateItem(index, { dispatchQuantity: staged > 0 ? 0 : remaining })}
                    />
                  </td>
                  <td className="px-3 py-3">{item.lineNumber ?? index + 1}</td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-slate-800">{item.itemName || '--'}</div>
                    {String(item.itemDescription || '').trim().toLowerCase() !== String(item.itemName || '').trim().toLowerCase() && (
                      <div className="text-slate-500">{item.itemDescription || ''}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">{invoicedQty(item)}</td>
                  <td className="px-3 py-3">{qtyOf(String(item.dispatchedQuantity ?? 0))}</td>
                  <td className="px-3 py-3">{remaining}</td>
                  <td className="px-3 py-3">
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      aria-label={`Dispatch qty, line ${item.lineNumber ?? index + 1}`}
                      disabled={frozen || isMarkedNotSupplied(item)}
                      title={isMarkedNotSupplied(item) ? DELIVERY_FIELD_NOTES.notSupplied : undefined}
                      value={item.dispatchQuantity || ''}
                      onChange={(e) => p.updateItem(index, { dispatchQuantity: qtyOf(e.target.value) }, true)}
                      className="h-8 w-24 rounded border border-slate-200 px-2 disabled:bg-slate-50"
                    />
                    {billedAfter != null && (
                      <p role="note" className="mt-1 w-40 text-amber-700">{DELIVERY_FIELD_NOTES.overRemaining(billedAfter)}</p>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <input
                      type="text"
                      maxLength={500}
                      aria-label={`Packing on this slip, line ${item.lineNumber ?? index + 1}`}
                      disabled={frozen}
                      value={item.dispatchPacking ?? item.itemPackingDetails ?? ''}
                      onChange={(e) => p.updateItem(index, { dispatchPacking: e.target.value }, true)}
                      className="h-8 w-48 rounded border border-slate-200 px-2 disabled:bg-slate-50"
                    />
                  </td>
                  <td className="px-3 py-3">{item.billUnit || item.unit || item.packingUnit || '--'}</td>
                  <td className="px-3 py-3">{item.sourceType || 'manual'}</td>
                  <td className="px-3 py-3">{readsAsInventory(item) ? INVENTORY_SUPPLIER : item.supplierName || '--'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
